import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { apiCreate, apiDelete, apiList, apiUpdate } from "@/lib/api";
import { useActiveClinicaId } from "@/hooks/useActiveClinicaId";

export interface Funcionario {
  id: string;
  user_id: string;
  nome: string;
  cargo: string;
  telefone: string;
  email: string;
  data_admissao: string | null;
  recebe_meta?: boolean;
  meta_periodo?: string;
  meta_inicio?: string;
  meta_fim?: string;
  meta_valor?: number;
  meta_bonus?: number;
  salario_base?: number;
  recebe_comissao?: boolean;
  percentual_comissao?: number;
  base_comissao?: "unidade" | "producao_pessoal";
  dentista_id_comissao?: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface CreateFuncionarioData {
  nome: string;
  cargo: string;
  telefone: string;
  email: string;
  data_admissao?: string;
  recebe_meta?: boolean;
  meta_periodo?: string;
  meta_inicio?: string;
  meta_fim?: string;
  meta_valor?: number;
  meta_bonus?: number;
  salario_base?: number;
  recebe_comissao?: boolean;
  percentual_comissao?: number;
  base_comissao?: "unidade" | "producao_pessoal";
  dentista_id_comissao?: string;
  status: string;
}

export function useFuncionarios() {
  const [funcionarios, setFuncionarios] = useState<Funcionario[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const activeClinicaId = useActiveClinicaId();

  const fetchFuncionarios = async () => {
    try {
      setLoading(true);
      const data = await apiList<Funcionario>("funcionarios");
      setFuncionarios((data || []).sort((a, b) => a.nome.localeCompare(b.nome)));
    } catch (error) {
      console.error("Error fetching funcionarios:", error);
      toast.error("Erro ao carregar funcionários");
    } finally {
      setLoading(false);
    }
  };

  const createFuncionario = async (funcionarioData: CreateFuncionarioData) => {
    if (!user) {
      toast.error("Usuário não autenticado");
      return false;
    }

    try {
      await apiCreate<Funcionario>("funcionarios", {
        ...funcionarioData,
        user_id: user.id,
      });

      toast.success("Funcionário criado com sucesso!");
      await fetchFuncionarios();
      return true;
    } catch (error) {
      console.error("Error creating funcionario:", error);
      toast.error("Erro ao criar funcionário");
      return false;
    }
  };

  const updateFuncionario = async (id: string, funcionarioData: Partial<CreateFuncionarioData>) => {
    try {
      await apiUpdate<Funcionario>("funcionarios", id, funcionarioData);

      toast.success("Funcionário atualizado com sucesso!");
      await fetchFuncionarios();
      return true;
    } catch (error) {
      console.error("Error updating funcionario:", error);
      toast.error("Erro ao atualizar funcionário");
      return false;
    }
  };

  const deleteFuncionario = async (id: string) => {
    try {
      await apiDelete("funcionarios", id);

      toast.success("Funcionário excluído com sucesso!");
      await fetchFuncionarios();
      return true;
    } catch (error) {
      console.error("Error deleting funcionario:", error);
      toast.error("Erro ao excluir funcionário");
      return false;
    }
  };

  useEffect(() => {
    if (user) {
      fetchFuncionarios();
    }
  }, [user?.id, activeClinicaId]);

  return {
    funcionarios,
    loading,
    createFuncionario,
    updateFuncionario,
    deleteFuncionario,
    refetch: fetchFuncionarios,
  };
}
